import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { Briefcase, Building2, Globe2, Wrench } from "lucide-react";
import type { ComponentType } from "react";
import { useEffect } from "react";

type IconHint = "wrench" | "office" | "globe" | "briefcase";

export type ClarificationOption = {
  id: string;
  label: string;
  tool: string;
  icon_hint?: IconHint;
};

const ICON_MAP: Record<IconHint, ComponentType<{ className?: string }>> = {
  wrench: Wrench,
  office: Building2,
  globe: Globe2,
  briefcase: Briefcase,
};

type Props = {
  options: ClarificationOption[];
  onPick: (optionId: string) => void;
  onSkip: () => void;
  className?: string;
};

/**
 * Quick clarification chips shown when the orchestrator detects an ambiguous
 * query. Users can pick an intention via click or skip (Esc / button). The
 * card stays visible until the user explicitly acts on it — results streaming
 * in do not dismiss it.
 */
export const SearchClarificationChips = ({
  options,
  onPick,
  onSkip,
  className,
}: Props) => {
  const { t } = useTranslation();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onSkip();
      }
    };
    window.addEventListener("keydown", onKey);

    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [onSkip]);

  return (
    <div
      className={cn(
        "mt-3 rounded-2xl border border-orange-500 bg-card p-4 shadow-sm",
        "animate-in slide-in-from-top-2 duration-200",
        className,
      )}
      role="region"
      aria-label={t("prospecting.search.clarification.aria")}
    >
      <div className="mb-3 flex items-center gap-2 text-sm text-muted-foreground">
        <span className="rounded-full bg-orange-500 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white">
          {t("prospecting.search.clarification.badge")}
        </span>
        <span className="text-foreground">
          {t("prospecting.search.clarification.helper")}
        </span>
      </div>

      <div className="flex flex-col items-start gap-2">
        {options.map((opt) => {
          const Icon = opt.icon_hint ? ICON_MAP[opt.icon_hint] : Globe2;
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => onPick(opt.id)}
              className={cn(
                "inline-flex items-center gap-2 rounded-full border border-border bg-muted px-3.5 py-2",
                "text-sm text-foreground transition",
                "hover:border-orange-500 hover:bg-orange-500/10 active:translate-y-[1px]",
              )}
            >
              <Icon className="h-4 w-4" />
              <span>{opt.label}</span>
            </button>
          );
        })}
        <button
          type="button"
          onClick={onSkip}
          className="inline-flex items-center gap-2 rounded-full border border-dashed border-border bg-transparent px-3.5 py-2 text-sm text-muted-foreground hover:border-foreground hover:text-foreground"
        >
          {t("prospecting.search.clarification.skip")}
        </button>
      </div>
    </div>
  );
};
